import { API_CONFIG, getDefaultHeaders } from '../config/api.config';

// Enhanced type definitions for comprehensive user activity logging
export interface UserActivity {
  id: string;
  realmId: string;
  userId: string;
  activityType: string;
  activityCategory: string;
  timestamp: string;
  ipAddress?: string;
  userAgent?: string;
  endpoint?: string;
  httpMethod?: string;
  statusCode?: number;
  responseTimeMs?: number;
  metadata: Record<string, any>;
  riskScore: number;
  createdAt: string;
  
  // Enhanced user context for legal analytics
  userName: string;
  userEmail?: string;
  isAttorney: boolean;
  userRoles: string[];
  dmsRole?: 'Attorney' | 'Paralegal' | 'Admin' | 'Client';
  userClassification: 'Attorney' | 'Staff' | 'Admin' | 'Administrator' | 'Client' | 'Unknown';
  firmName?: string;
  lastLoginAt?: string;
  
  // Human-readable display fields
  activityDescription: string;
  riskLevel: string;
  riskDescription: string;
  riskColor: string;
}

export interface SecurityEvent {
  id: string;
  realmId: string;
  userId?: string;
  eventType: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  sourceIp?: string;
  userAgent?: string;
  eventData: Record<string, any>;
  threatIndicators: string[];
  investigationStatus: 'open' | 'investigating' | 'closed';
  timestamp: string;
  createdAt: string;
}

export interface SecurityAlert {
  id: string;
  realmId: string;
  alertType: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description?: string;
  affectedUserIds: string[];
  sourceEventIds: string[];
  status: 'active' | 'investigating' | 'resolved';
  assignedTo?: string;
  alertData: Record<string, any>;
  autoResolved: boolean;
  resolutionNotes?: string;
  createdAt: string;
  resolvedAt?: string;
}

export interface ActivityTypeOption {
  type: string;
  count: number;
  label: string;
}

export interface ActivityTypesByCategory {
  [category: string]: ActivityTypeOption[];
}

export interface ActivityFilters {
  userId?: string;
  userName?: string;  // For partial name search
  activityType?: string;
  activityCategory?: string;
  startDate?: string;
  endDate?: string;
  riskScoreMin?: number;
  riskScoreMax?: number;
  ipAddress?: string;
  endpoint?: string;
  httpMethod?: string;
  statusCode?: number;
  page?: number;
  limit?: number;
  
  // Enhanced user-based filters for legal analytics
  userClassification?: 'Attorney' | 'Staff' | 'Admin' | 'Client';
  isAttorney?: boolean;
  permissionLevel?: 'High' | 'Medium' | 'Low';
  userRoles?: string[];
  dmsRole?: 'Attorney' | 'Paralegal' | 'Admin' | 'Client';
  firmName?: string;
  activityRiskContext?: 'Attorney_Action' | 'Staff_Action' | 'Admin_Action' | 'Client_Action';
  metadata?: Record<string, string | number | boolean>;
}

export interface UserSearchResult {
  id: string;
  name: string;
  email: string;
}

export interface SecurityEventFilters {
  userId?: string;
  eventType?: string;
  severity?: string;
  sourceIp?: string;
  investigationStatus?: string;
  startDate?: string;
  endDate?: string;
  threatIndicators?: string[];
  page?: number;
  limit?: number;
}

export interface SecurityAlertFilters {
  status?: string;
  priority?: string;
  alertType?: string;
  assignedTo?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedActivities {
  activities: UserActivity[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedSecurityEvents {
  events: SecurityEvent[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface PaginatedSecurityAlerts {
  alerts: SecurityAlert[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface AnalyticsData {
  period: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  activityCounts: Array<{
    type: string;
    count: number;
  }>;
  highRiskCount: number;
  uniqueActiveUsers: number;
  totalActivities: number;
  securityMetrics: {
    failedAttempts: number;
    afterHoursActivity: number;
    averageRiskScore: number;
    uniqueLocations: number;
  };
}

export interface UserBehaviorAnalysis {
  userId: string;
  totalActivities: number;
  averageRiskScore: number;
  mostCommonActivities: Array<{
    type: string;
    count: number;
  }>;
  riskTrend: Array<{
    date: string;
    avgRisk: number;
  }>;
  securityIncidents: SecurityEvent[];
  lastActivity: string;
}

export interface ComplianceReportData {
  reportType: string;
  generatedAt: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalActivities: number;
    uniqueUsers: number;
    securityEvents: number;
    highRiskActivities: number;
  };
  activities: UserActivity[];
  securityEvents: SecurityEvent[];
  metadata: Record<string, any>;
}

export interface ComplianceReportHistoryItem {
  id: string;
  reportType: string;
  startDate: string;
  endDate: string;
  generatedAt: string;
  status: 'ready' | 'failed';
  includeActivities: boolean;
  includeSecurityEvents: boolean;
}

export interface PaginatedComplianceReportHistory {
  reports: ComplianceReportHistoryItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LoggingReportSchedule {
  enabled: boolean;
  frequency: 'weekly' | 'monthly';
  reportType?: string;
  rangeDays?: number;
  includeActivities?: boolean;
  includeSecurityEvents?: boolean;
  nextRun?: string;
}

export interface SecurityActivitySummary {
  period: string;
  summary: {
    totalSecurityEvents: number;
    criticalEvents: number;
    highRiskActivities: number;
    activeThreats: number;
  };
  categories: Array<{
    name: string;
    count: number;
    severity: 'low' | 'medium' | 'high' | 'critical';
    trend: 'increasing' | 'decreasing' | 'stable';
  }>;
  topRisks: Array<{
    type: string;
    count: number;
    lastOccurrence: string;
  }>;
}

export interface SecurityRecommendations {
  period: string;
  recommendations: Array<{
    id: string;
    title: string;
    description: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
    category: string;
    actionRequired: boolean;
    estimatedImpact: string;
  }>;
}

export interface ThreatContext {
  currentThreatLevel: 'low' | 'medium' | 'high' | 'critical';
  activeThreats: number;
  recentIncidents: Array<{
    type: string;
    timestamp: string;
    severity: string;
    resolved: boolean;
  }>;
  threatSources: Array<{
    source: string;
    count: number;
    lastActivity: string;
  }>;
}

export interface SecurityCategory {
  name: string;
  score: number;
  status: 'good' | 'warning' | 'critical';
  events: number;
  lastEvent?: Date;
  riskFactors?: {
    frequency: number;
    timing: number;
    userBehavior: number;
    geographic: number;
  };
}

export interface SecurityDashboard {
  status: 'all_clear' | 'monitoring' | 'attention_needed' | 'critical' | 'degraded';
  categories: {
    authentication: SecurityCategory;
    dataAccess: SecurityCategory;
    activityMonitoring: SecurityCategory;
    userManagement: SecurityCategory;
    financial: SecurityCategory;
  };
  alerts: {
    total: number;
    byPriority: Record<string, number>;
  };
  summary: {
    totalEvents: number;
    riskScore: number;
    lastActivity: Date;
  };
  metadata?: {
    dataQuality: 'live' | 'degraded';
    message?: string;
  };
}

export interface SecurityPostureScore {
  overallScore: number;
  maxScore: number;
  breakdown: Array<{
    category: string;
    score: number;
    maxScore: number;
    weight: number;
  }>;
  trends: Array<{
    date: string;
    score: number;
  }>;
  recommendations: string[];
  dataQuality?: {
    state: 'live' | 'degraded';
    message?: string;
  };
}

export interface SystemHealth {
  status: 'healthy' | 'unhealthy';
  database: {
    connected: boolean;
    lastActivity?: string;
    recentActivityCount?: number;
  };
  queues: {
    userActivity: QueueStatus;
    securityEvents: QueueStatus;
    securityAlerts: QueueStatus;
  };
  alerts: {
    activeCount: number;
  };
  timestamp: string;
}

export interface QueueStatus {
  waiting: number;
  active: number;
  failed: number;
}

export interface DetailedSystemHealth extends SystemHealth {
  components: {
    database: {
      status: 'healthy' | 'unhealthy';
      lastActivity?: string;
      recentActivityCount?: number;
    };
    queues: {
      status: 'healthy' | 'unhealthy';
      details: {
        userActivity: QueueStatus;
        securityEvents: QueueStatus;
        securityAlerts: QueueStatus;
      };
    };
    logging: {
      recentActivitiesCount: number;
      activeAlertsCount: number;
      lastHourActivity: boolean;
    };
  };
  metrics: {
    recentActivities: number;
    activeAlerts: number;
    queueCounts: {
      userActivity: QueueStatus;
      securityEvents: QueueStatus;
      securityAlerts: QueueStatus;
    };
  };
}

// API Response wrapper
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  timestamp: string;
}

// =====================================
// LEX (AI EXPLAINER) TYPES
// =====================================

export type LexExplainType =
  | 'user_activity'
  | 'security_dashboard'
  | 'security_category'
  | 'security_alerts_overview';

export interface LexExplainRequest {
  type: LexExplainType;
  payload: any;
}

export interface LexExplainResponse {
  explanation: string;
  provider: string;
  model: string;
}

// =====================================
// LEX SECURITY AGENT (GROUNDED EVIDENCE)
// =====================================

export type LexSecurityTimeframe = '1h' | '24h' | '7d' | '30d';
export type LexSecurityReferenceKind =
  | 'security_events'
  | 'security_alerts'
  | 'security_dashboard'
  | 'security_posture'
  | 'user_activities';

export interface LexSecurityReference {
  id: string;
  kind: LexSecurityReferenceKind;
  label: string;
  description?: string;
  deepLink?: string;
  filters: Record<string, any>;
}

export interface LexSecurityExplainData {
  explanation: string;
  provider?: string;
  model?: string;
  timeframe: LexSecurityTimeframe;
  references: LexSecurityReference[];
  reference?: LexSecurityReference;
  stats?: Record<string, any>;
  evidence?: Record<string, any>;
}

/**
 * EdgeLex User Logging API Service
 * Provides type-safe access to all user logging endpoints
 */
export class LoggingApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_CONFIG.BASE_URL;
  }

  private async makeRequest<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const token = localStorage.getItem('webelieve_access_token') || undefined;
    const headers = getDefaultHeaders(token);

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        ...headers,
        ...options?.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // =====================================
  // USER ACTIVITY ENDPOINTS
  // =====================================

  /**
   * Get distinct activity types from backend API
   */
  async getDistinctActivityTypes(): Promise<ActivityTypesByCategory> {
    const response = await this.makeRequest<{ activityTypes: ActivityTypesByCategory }>(
      '/api/v1/logging/activity-types'
    );
    return response.activityTypes;
  }

  /**
   * Search users by name or email for autocomplete
   */
  async searchUsers(query: string): Promise<UserSearchResult[]> {
    if (!query || query.length < 2) {
      return [];
    }
    const response = await this.makeRequest<{ users: UserSearchResult[] }>(
      `/api/v1/logging/users/search?q=${encodeURIComponent(query)}`
    );
    return response.users;
  }

  /**
   * Get user activities with filtering and pagination
   */
  async getUserActivities(filters: ActivityFilters = {}): Promise<PaginatedActivities> {
    const params = new URLSearchParams();
    const { metadata, ...flatFilters } = filters;
    Object.entries(flatFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value));
      }
    });
    if (metadata) {
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(`metadata.${key}`, String(value));
        }
      });
    }

    const response = await this.makeRequest<ApiResponse<PaginatedActivities>>(
      `/api/v1/logging/activities?${params.toString()}`
    );
    return response.data;
  }

  /**
   * Get specific user activity by ID
   */
  async getActivityById(activityId: string): Promise<UserActivity> {
    const response = await this.makeRequest<ApiResponse<UserActivity>>(
      `/api/v1/logging/activities/${activityId}`
    );
    
    return response.data;
  }

  /**
   * Get recent user activities (configurable hours)
   */
  async getRecentActivities(hours: number = 24): Promise<UserActivity[]> {
    const response = await this.makeRequest<ApiResponse<UserActivity[]>>(
      `/api/v1/logging/activities/recent?hours=${hours}`
    );
    
    return response.data;
  }

  // =====================================
  // SECURITY EVENT ENDPOINTS
  // =====================================

  /**
   * Get security events with filtering and pagination
   */
  async getSecurityEvents(filters: SecurityEventFilters = {}): Promise<PaginatedSecurityEvents> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (key === 'threatIndicators' && Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, value.toString());
        }
      }
    });

    const response = await this.makeRequest<ApiResponse<PaginatedSecurityEvents>>(
      `/api/v1/logging/security-events?${params.toString()}`
    );
    
    return response.data;
  }

  /**
   * Get specific security event by ID
   */
  async getSecurityEventById(eventId: string): Promise<SecurityEvent> {
    const response = await this.makeRequest<ApiResponse<SecurityEvent>>(
      `/api/v1/logging/security-events/${eventId}`
    );
    
    return response.data;
  }

  // =====================================
  // SECURITY ALERT ENDPOINTS
  // =====================================

  /**
   * Get security alerts with filtering and pagination
   */
  async getSecurityAlerts(filters: SecurityAlertFilters = {}): Promise<PaginatedSecurityAlerts> {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });

    const response = await this.makeRequest<ApiResponse<PaginatedSecurityAlerts>>(
      `/api/v1/logging/security-alerts?${params.toString()}`
    );
    
    return response.data;
  }

  /**
   * Get active security alerts
   */
  async getActiveAlerts(): Promise<SecurityAlert[]> {
    const response = await this.makeRequest<ApiResponse<SecurityAlert[]>>(
      `/api/v1/logging/security-alerts/active`
    );
    
    return response.data;
  }

  /**
   * Resolve a security alert
   */
  async resolveAlert(alertId: string, notes?: string): Promise<SecurityAlert> {
    const response = await this.makeRequest<ApiResponse<SecurityAlert>>(
      `/api/v1/logging/security-alerts/${alertId}/resolve`,
      {
        method: 'POST',
        body: JSON.stringify({ notes })
      }
    );
    
    return response.data;
  }

  /**
   * Assign a security alert to a team member
   */
  async assignAlert(alertId: string, assignee: string): Promise<SecurityAlert> {
    const response = await this.makeRequest<ApiResponse<SecurityAlert>>(
      `/api/v1/logging/security-alerts/${alertId}/assign`,
      {
        method: 'POST',
        body: JSON.stringify({ assignee })
      }
    );
    
    return response.data;
  }

  // =====================================
  // ANALYTICS ENDPOINTS
  // =====================================

  /**
   * Get system analytics and metrics
   */
  async getAnalytics(period: '1h' | '24h' | '7d' | '30d' = '24h', metric?: string): Promise<AnalyticsData> {
    const params = new URLSearchParams({ period });
    if (metric) {
      params.append('metric', metric);
    }

    const response = await this.makeRequest<ApiResponse<AnalyticsData>>(
      `/api/v1/logging/analytics?${params.toString()}`
    );
    
    return response.data;
  }

  /**
   * Get user behavior analysis
   */
  async getUserBehaviorAnalysis(userId: string): Promise<UserBehaviorAnalysis> {
    const response = await this.makeRequest<ApiResponse<UserBehaviorAnalysis>>(
      `/api/v1/logging/analytics/user-behavior/${userId}`
    );
    
    return response.data;
  }

  // =====================================
  // REPORTING ENDPOINTS
  // =====================================

  async getComplianceReportHistory(params?: {
    page?: number;
    limit?: number;
    reportType?: string;
  }): Promise<PaginatedComplianceReportHistory> {
    const qs = new URLSearchParams();
    if (params?.page) qs.append('page', String(params.page));
    if (params?.limit) qs.append('limit', String(params.limit));
    if (params?.reportType) qs.append('reportType', params.reportType);

    const response = await this.makeRequest<ApiResponse<PaginatedComplianceReportHistory>>(
      `/api/v1/logging/reports/history?${qs.toString()}`
    );
    return response.data;
  }

  async downloadComplianceReport(reportId: string): Promise<ComplianceReportData> {
    const response = await this.makeRequest<ApiResponse<ComplianceReportData>>(
      `/api/v1/logging/reports/${encodeURIComponent(reportId)}/download`
    );
    return response.data;
  }

  async getReportSchedule(): Promise<LoggingReportSchedule> {
    const response = await this.makeRequest<ApiResponse<LoggingReportSchedule>>(
      `/api/v1/logging/reports/schedule`
    );
    return response.data;
  }

  async updateReportSchedule(schedule: Partial<LoggingReportSchedule>): Promise<LoggingReportSchedule> {
    const response = await this.makeRequest<ApiResponse<LoggingReportSchedule>>(
      `/api/v1/logging/reports/schedule`,
      {
        method: 'PUT',
        body: JSON.stringify(schedule),
      }
    );
    return response.data;
  }

  /**
   * Generate compliance report
   */
  async generateComplianceReport(
    reportType: string,
    startDate: string,
    endDate: string,
    options?: {
      includeActivities?: boolean;
      includeSecurityEvents?: boolean;
    }
  ): Promise<ComplianceReportData> {
    const response = await this.makeRequest<ApiResponse<ComplianceReportData>>(
      `/api/v1/logging/reports/compliance`,
      {
        method: 'POST',
        body: JSON.stringify({
          reportType,
          startDate,
          endDate,
          ...options
        })
      }
    );
    
    return response.data;
  }

  // =====================================
  // SYSTEM HEALTH ENDPOINTS
  // =====================================

  /**
   * Get basic system health
   */
  async getSystemHealth(): Promise<SystemHealth> {
    const response = await this.makeRequest<ApiResponse<SystemHealth>>(
      `/api/v1/logging/health`
    );
    
    return response.data;
  }

  /**
   * Get detailed system health with comprehensive metrics
   */
  async getDetailedSystemHealth(): Promise<DetailedSystemHealth> {
    const response = await this.makeRequest<ApiResponse<DetailedSystemHealth>>(
      `/api/v1/logging/health/detailed`
    );
    
    return response.data;
  }

  // =====================================
  // TESTING ENDPOINTS (development)
  // =====================================

  /**
   * Log test activity (for testing purposes)
   */
  async logTestActivity(activityData: {
    activityType: string;
    activityCategory?: string;
    riskScore?: number;
    metadata?: Record<string, any>;
  }): Promise<void> {
    await this.makeRequest<ApiResponse<void>>(
      `/api/v1/logging/test/activity`,
      {
        method: 'POST',
        body: JSON.stringify(activityData)
      }
    );
  }

  /**
   * Log test security event (for testing purposes)
   */
  async logTestSecurityEvent(eventData: {
    eventType: string;
    severity?: string;
    threatIndicators?: string[];
    eventData?: Record<string, any>;
  }): Promise<void> {
    await this.makeRequest<ApiResponse<void>>(
      `/api/v1/logging/test/security-event`,
      {
        method: 'POST',
        body: JSON.stringify(eventData)
      }
    );
  }

  // =====================================
  // SECURITY ANALYTICS ENDPOINTS
  // =====================================

  /**
   * Get security dashboard overview
   */
  async getSecurityDashboard(timeframe: string = '7d'): Promise<SecurityDashboard> {
    const response = await this.makeRequest<ApiResponse<SecurityDashboard>>(
      `/api/v1/logging/security/dashboard?timeframe=${timeframe}`
    );
    return response.data;
  }

  /**
   * Get security posture score and breakdown
   */
  async getSecurityPostureScore(timeframe: string = '7d'): Promise<SecurityPostureScore> {
    const response = await this.makeRequest<ApiResponse<SecurityPostureScore>>(
      `/api/v1/logging/security/posture-score?timeframe=${timeframe}`
    );
    return response.data;
  }

  /**
   * Get security activity summary
   */
  async getSecurityActivitySummary(timeframe: string = '24h'): Promise<SecurityActivitySummary> {
    const response = await this.makeRequest<ApiResponse<SecurityActivitySummary>>(
      `/api/v1/logging/security/activity-summary?timeframe=${timeframe}`
    );
    return response.data;
  }

  /**
   * Get security recommendations
   */
  async getSecurityRecommendations(timeframe: string = '7d'): Promise<SecurityRecommendations> {
    const response = await this.makeRequest<ApiResponse<SecurityRecommendations>>(
      `/api/v1/logging/security/recommendations?timeframe=${timeframe}`
    );
    return response.data;
  }

  /**
   * Get current threat context
   */
  async getThreatContext(): Promise<ThreatContext> {
    const response = await this.makeRequest<ApiResponse<ThreatContext>>(
      `/api/v1/logging/security/threat-context`
    );
    return response.data;
  }

  // =====================================
  // LEX (AI EXPLAINER) ENDPOINTS
  // =====================================

  /**
   * Explain security/logging posture using grounded evidence (LexSecurityAgent).
   */
  async explainSecurityOverview(params: {
    timeframe: LexSecurityTimeframe;
    includeEvidence?: boolean;
    maxEvidenceEvents?: number;
    maxEvidenceAlerts?: number;
    sessionId?: string;
  }): Promise<LexSecurityExplainData> {
    const response = await this.makeRequest<ApiResponse<LexSecurityExplainData>>(
      `/api/v1/logging/lex/security/overview`,
      {
        method: 'POST',
        body: JSON.stringify(params)
      }
    );
    return response.data;
  }

  /**
   * Explain a specific evidence slice (events/alerts/activities) using grounded retrieval.
   */
  async explainSecurityReference(params: {
    timeframe?: LexSecurityTimeframe;
    reference: LexSecurityReference;
    maxItems?: number;
    sessionId?: string;
  }): Promise<LexSecurityExplainData> {
    const response = await this.makeRequest<ApiResponse<LexSecurityExplainData>>(
      `/api/v1/logging/lex/security/reference`,
      {
        method: 'POST',
        body: JSON.stringify(params)
      }
    );
    return response.data;
  }

  /**
   * Explain a logging/security UI object using Lex (AI).
   */
  async explainWithLex(request: LexExplainRequest): Promise<LexExplainResponse> {
    const response = await this.makeRequest<ApiResponse<LexExplainResponse>>(
      `/api/v1/logging/lex/explain`,
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    );
    return response.data;
  }
}

// Export singleton instance
export const loggingApiService = new LoggingApiService(); 
